import { Component, ElementRef, EventEmitter, Input, Output, Renderer2, TemplateRef } from '@angular/core';
import { BehaviorSubject, combineLatest, defer, from, Subject } from 'rxjs';
import { MemoizationPromiseFnBuilder, EnergiesService, isNotNullOrUndefined, Range, System, today } from 'ngx-core-services';
import { PageEvent } from '@angular/material/paginator';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, first, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { selectSystemById, SystemDefinition, SystemsState } from '../../store';
import { selectSystemEnergyStateBySystemUidAndDefaultInstance } from '../../store/selectors/totalizer.selectors';
import { dailyEnergyStateBySystemUidAndDefaultInstance } from '../../store/selectors/daily-state-overview.selectors';
import { FormBuilder } from '@angular/forms';

const labels = {
  connectedAndUpdated: 'Connected and updated',
  dailyProductionEnergy: 'Daily production energy',
  disconnected: 'Disconnected',
  noResults: 'No results',
  notUpdated: 'Not updated',
  onlyCertified: 'Only certified',
  onlyNotCertified: 'Only not certified',
  searchOnTable: 'Search on table',
  systemAlerts: 'Alerts',
  systemDevices: 'Devices',
  systemName: 'Name',
  systemStatus: 'Status',
  title: 'My Plants',
  totalProductionEnergy: 'Total production energy',
  updated: 'Updated',
  hasAlerts: 'Has Alerts',
};

const trackByFn = (_: number, item: any): string => {
  return item.system.uid;
}

@CreateCustomBlockSizeType({})
@Component({
  selector: 'lib-systems-list',
  templateUrl: './systems-list.component.html',
  styleUrls: ['./systems-list.component.scss'],
})
export class SystemsListComponent implements ICustomBlock {
  readonly systems$ = new BehaviorSubject<System[] | null | undefined>(null);
  @Input() set systems(value: System[] | null | undefined) {
    this.systems$.next(value);
  }
  get systems(): System[] | null | undefined {
    return this.systems$.value;
  }

  @Input() refresh$ = new Subject<void>();
  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }

  get labels(): typeof labels{
    return this._labels;
  }

  readonly query$ = new BehaviorSubject<string>('');
  @Input() set query(value: string) {
    this.query$.next(value);
  }
  get query(): string {
    return this.query$.value;
  }

  @Input() filterMode: 'all' | 'only-certified' | 'only-base' = 'all';
  @Input() compactMode: boolean = false;
  @Input() hideColumns: string[] = [];
  @Input() actionsTemplate: TemplateRef<any> | null = null;

  @Output() readonly navigateToSystem = new EventEmitter<System>();

  readonly trackByFn = trackByFn;
  readonly tablePagination$ = new BehaviorSubject<{pageSize: number, pageIndex: number}>(
    { pageSize: 10, pageIndex: 0 }
  );

  readonly filters = this.formBuilder.group({
    certified: false,
    noCertified: false,
  });

  readonly filteredListCount = new BehaviorSubject<number>(0);
  readonly paginatedSystems$ = combineLatest([
    this.systems$.pipe(
      distinctUntilChanged((a, b) => {
        const sortedUidsA = (a ?? []).map((system) => system.uid).sort().toString();
        const sortedUidsB = (b ?? []).map((system) => system.uid).sort().toString();
        return sortedUidsA === sortedUidsB;
      }),
    ),
    this.tablePagination$,
    this.query$.pipe(
      distinctUntilChanged(),
    ),
    this.filters.valueChanges.pipe(
      distinctUntilChanged((a, b) => {
        return a.certified === b.certified && a.noCertified === b.noCertified;
      }),
      startWith(this.filters.value),
    ),
  ]).pipe(
    map(([systems, pagination, query]) => {
      query = query.toLowerCase();

      const filteredSystems = systems
        ?.filter((system) => {
          if (this.filterMode === 'only-certified' && system.source !== 'aws') {
            return false;
          }
          if (this.filterMode === 'only-base' && system.source !== 'firebase') {
            return false;
          }

          if (this.filterMode === 'all') {
            const isCertified = this.filters.get('certified')?.value ?? false;
            const isNotCertified = this.filters.get('noCertified')?.value ?? false;

            if (isCertified && isNotCertified) {
              return true;
            }

            if (isCertified && system.source === 'firebase') {
              return false;
            }

            if (isNotCertified && system.source === 'aws') {
              return false;
            }
          }

          return true;
        })
        ?.filter((system) =>
          system.name.toLowerCase().includes(query) ||
          system.connextSn?.toLowerCase().includes(query) ||
          system.configuration.connextId?.toLowerCase().includes(query) ||
          system.devices
            .map((device) => device.id)
            .some((id) => id.toLowerCase().includes(query))
        );

      if (filteredSystems) {
        this.filteredListCount.next(filteredSystems.length);
        return filteredSystems.slice(pagination.pageIndex * pagination.pageSize, (pagination.pageIndex + 1) * pagination.pageSize);
      }
      return [];
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly dataSource$ = this.paginatedSystems$.pipe(
    switchMap((systems) =>
      Promise.all(
        systems.map(async (system) => {
          const _this = this;

          const details: {
            system: System;
            name: string;
            dailyProductionEnergy: number | null;
            totalProductionEnergy: number | null;
            updateDetails$: any;
          } = {
            system: system,
            name: system.name,
            dailyProductionEnergy: null,
            totalProductionEnergy: null,
            updateDetails$: defer((): any => {
              return from(
                (async () => {
                  const _system = await _this.store
                    .select(selectSystemById(system.uid!))
                    .pipe(isNotNullOrUndefined(), first())
                    .toPromise();

                  const totalEnergiesState = await _this.store
                    .select(selectSystemEnergyStateBySystemUidAndDefaultInstance(system.uid!))
                    .pipe(first())
                    .toPromise();

                  const dailyEnergiesState = await _this.store
                    .select(dailyEnergyStateBySystemUidAndDefaultInstance(system.uid!))
                    .pipe(first())
                    .toPromise();

                  if (totalEnergiesState && dailyEnergiesState) {
                    details.dailyProductionEnergy = Number(dailyEnergiesState.EG1) ?? null;
                    details.totalProductionEnergy = Number(totalEnergiesState.ET1) ?? null;

                    return details;
                  }

                  const aggregates = await _this.getProduction(_system);
                  details.dailyProductionEnergy = aggregates.energyDailyGenerating;
                  details.totalProductionEnergy = aggregates.energyTotalGenerating;

                  return details;
                })()
              );
            }).pipe(
              shareReplay(1),
            ),
          };

          return details;
        })
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  displayedColumns = ['system-status', 'name', 'alerts', 'devices', 'dailyProductionEnergy', 'totalProductionEnergy'];

  get computedDisplayedColumns(): string[] {
    let columns: string[];

    if (this.compactMode) {
      columns = ['name', 'alerts', 'devices'];
    } else {
      columns = this.displayedColumns.filter((col) => !this.hideColumns.includes(col));
    }

    if (this.actionsTemplate) {
      columns.push('actions');
    }

    return columns;
  }

  shouldShowAlertForSystem(system: System): boolean {
    return !system.configuration.connextId;
  }

  pageSizeOptions = [10, 25];

  constructor(
    private readonly energiesService: EnergiesService,
    readonly el: ElementRef,
    private readonly formBuilder: FormBuilder,
    readonly renderer: Renderer2,
    readonly store: Store<SystemsState>,
  ) {
    const expirationTime = 1000 * 60 * 5;
    this.energiesService.fetchEnergiesByRange = MemoizationPromiseFnBuilder(this.energiesService.fetchEnergiesByRange, expirationTime, this.energiesService);

    this.getProduction = this.getProduction.bind(this);
  }

  async getProduction(_system: SystemDefinition) {
    let inverterSerials = _system.devices
      .filter((device) => device.type !== 'connext')
      .map((device) => device.id);
    const verifiedUninstallations = (
      _system.metadata?.verifiedUninstallations ?? []
    ).map((device) => device.sn);
    inverterSerials = inverterSerials.concat(verifiedUninstallations);

    const connextSerial = _system.configuration.connextId || '';
    const azzurroHubId = _system.configuration.azzurroHubId || '';

    const serials =
      _system.monitoringType === 'Solarman'
        ? inverterSerials
        : _system.monitoringType === 'Connext'
          ? [connextSerial]
          : _system.monitoringType === 'AzzurroHub'
            ? [azzurroHubId]
            : inverterSerials;

    const range: Range = {
      from: today(),
      to: today(),
    };

    const energies = await this.energiesService.fetchEnergiesByRange(
      serials,
      _system.monitoringType,
      range
    );

    return Object.values(energies).reduce(
      (acc, inv) => {
        const t = inv.pop() ?? {};
        return {
          energyDailyGenerating: (acc.energyDailyGenerating ?? 0) + parseFloat(t?.EG1 ?? '0'),
          energyTotalGenerating: (acc.energyTotalGenerating ?? 0) + parseFloat(t?.ET1 ?? '0'),
          TS: Number(t.TS),
        };
      }, {} as {
        energyDailyGenerating: number;
        energyTotalGenerating: number;
        TS: number;
      });
  }

  paginationChange($event: PageEvent) {
    this.tablePagination$.next($event);
  }
}
